
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Mic, MicOff, Download, Trash2, MessageSquare, Wifi, WifiOff } from 'lucide-react';
import { TranscriptEntry } from './TranscriptEntry';
import { useTwilioContext } from '@/context/TwilioContext';


interface LiveTranscriptProps {
  isActive: boolean;
  onToggle: (active: boolean) => void;
}

export const LiveTranscript = ({ isActive, onToggle }: LiveTranscriptProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { 
    callInProgress,
    transcript,
    isListening,
    isSupported,
    startTranscription,
    stopTranscription,
    clearTranscript,
    downloadTranscript,
    backendConnected
  } = useTwilioContext();


  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [transcript]);

  // Auto-start transcription when call begins
  useEffect(() => {
    if (callInProgress && !isListening && isSupported) {
      startTranscription();
    }
  }, [callInProgress, isListening, isSupported, startTranscription]);

  // Handle toggle for parent component
  useEffect(() => {
    onToggle(isListening || backendConnected);
  }, [isListening, backendConnected, onToggle]);

  if (!isSupported) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="p-6 text-center">
          <MicOff className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">Speech Recognition Not Supported</h3>
          <p className="text-sm text-muted-foreground">
            Your browser doesn't support speech recognition. Try using Chrome or Edge for live transcription.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Controls */}

      {/* Transcript Area */}
      <ScrollArea ref={scrollAreaRef} className="flex-1 bg-background/10">
        <div className="p-6 space-y-4">
          {transcript.length === 0 ? (
            <div className="text-center py-12">
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-primary/10 to-accent/10 rounded-full" />
                </div>
                <MessageSquare className="h-12 w-12 mx-auto relative z-10 text-muted-foreground" />
              </div>
              <h3 className="font-medium text-foreground mb-2">Real-time Transcription Ready</h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                {callInProgress 
                  ? "Start speaking and see your words appear here in real-time" 
                  : "Start a call and transcription will begin automatically"
                }
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {transcript.map((message) => (
                <TranscriptEntry
                  key={message.id}
                  message={message}
                />
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
